import { isEmpty } from "lodash";
import reduxStore from "../../configureEnvStore";
import { ENABLED, LOCAL_STORAGE_KEYS, MAP_IMAGE_KEY, MAP_IMAGE_URL, MEDIASTORAGE_URL } from "../../constants/Constants";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import { handleShowNetworkUnstableView } from "../Network/NetworkUtils";
import { setIsLocationOnVideoIsVisible, setJoinedUserUserId, setLocationOnVideoDetails } from "../../reducers/common-reducer";
import { addCallParticipant, getParticipantIndex, sendDataTrack } from "../../shared/dataTrackFunction";
import Localize from "../../locale/Localize";
import { isParticipantStreaming, showLocationError, showLocationSuccess } from "../Twilio/Room/roomFunction";
import { setStreamingDetails } from "../../reducers/twilioEvents-reducer";
import { NOTIFICATION_TYPES } from "../../constants/DataTrackNotificationConstants";
import {
    setIsLatLongDisable,
    setIsSaveBtnDisabled,
    setMapTypeId,
    setStableLocationCount,
    setUpdateLatLong,
} from "../../reducers/live-location-reducer";
import CustomToast from "../UI/customToast";
import { getCurrentPosition, getUpdatedCoordinates } from "../../shared/geolocationPermission";
import storage from "../../services/storage";
import { LogFirebase } from "../../services/firestore";
import watermarkImageService from "../../services/WatermarkImage/watermarkImageService";
import takePhotoService from "../../services/takePhotoService";

const GOOGLE_MAP_EXTRA_OPTIONS = "&zoom=18&size=640x640&sensor=false";
const START_MARKER = "markers=color:blue%7Clabel:L%7C";
const END_MARKER = "markers=icon:" + MEDIASTORAGE_URL + "misc/end.png" + "%7C";

/*  ***********  Function to add lat-long div under video tag  *********** */
export const addRemoveLatLong = (latitude, longitude, networkQuality) => {
    const { pointerOn } = reduxStore.getState().livePointer;
    const { streamingPartiIdentity, videoStreamingUsers, screenSharingUsers } = reduxStore.getState().twilioEvents;
    const { liveCaptionViewOn } = reduxStore.getState().liveCaption;

    //show net quality view..
    handleShowNetworkUnstableView(networkQuality);

    //do not show location view if pointer is On
    if (pointerOn || (liveCaptionViewOn && PublicVariables.isMobile)) return;

    const isLocalUserStreaming = streamingPartiIdentity === CALL_DATA.localIdentity;
    const isAnyVideoOrScreenShareEnabled = !isEmpty(videoStreamingUsers) || !isEmpty(screenSharingUsers);
    //lat long will be shown only when video is on and guest's location info is ON
    if (
        (GLOBAL_MEETING_DATA.isAgent && isLocalUserStreaming && PublicVariables.featureFlag?.AGENTLOCATION === ENABLED) || // For Agent
        (isAnyVideoOrScreenShareEnabled && !isLocalUserStreaming && PublicVariables.featureFlag?.ENABLELOCATION === ENABLED) // For Guest
    ) {
        if (GLOBAL_MEETING_DATA.isAgent) {
            if (latitude == null || latitude == undefined || latitude === "") {
                //don't show any message
                console.log("::No lat long to show::");
            } else {
                const isMeasurementStarted = reduxStore.getState().measurement.measurementStarted;
                // Do not show location when measurement is started
                if (!isMeasurementStarted) {
                    //html needs to be set in js as we need to add it after video stream comes.
                    if (latitude == 0 || longitude == 0) {
                        //when user has denied location permission
                        reduxStore.dispatch(setLocationOnVideoDetails({}));
                    } else {
                        let participantName = Localize[PublicVariables.currentLanguageObj.languageCode]["participant"];
                        let participantIndex = getParticipantIndex(streamingPartiIdentity);

                        if (isLocalUserStreaming) {
                            participantName = GLOBAL_MEETING_DATA.blitzzUserName;
                        } else if (participantIndex) {
                            participantName = participantIndex;
                        }
                        //check for decimal length
                        const decimalLen = latitude.toString().split(".")[1].length;
                        if (decimalLen != 4) {
                            latitude = latitude.toFixed(4);
                            longitude = longitude.toFixed(4);
                        }
                        const googleMapLink = "https://maps.google.com?q=" + latitude + "," + longitude;

                        reduxStore.dispatch(
                            setLocationOnVideoDetails({
                                latitude,
                                longitude,
                                googleMapLink,
                                participantName,
                            })
                        );
                    }
                    reduxStore.dispatch(setIsLocationOnVideoIsVisible(true));
                }
            }
        }
    } else {
        reduxStore.dispatch(setIsLocationOnVideoIsVisible(false));
    }
};

export const showLatLongForStreaming = () => {
    const { callParticipants } = reduxStore.getState().commonReducer;
    console.log("show lat-long for streaming called");
    callParticipants.forEach((user) => {
        if (isParticipantStreaming(user.identity)) {
            reduxStore.dispatch(setStreamingDetails(user));
            addRemoveLatLong(user.latitude, user.longitude, user.networkQuality);
        }
    });
};

// For Sending 207 data track info
export const sendLocationInfo = (extraInfo = {}) => {
    const { isRemoteParticipantJoined } = reduxStore.getState().commonReducer;

    if (isRemoteParticipantJoined) {
        let msg = {
            notification_type: NOTIFICATION_TYPES.LOCATION,
            allowedLocation: PublicVariables.allowedLocation,
            identity: CALL_DATA.localIdentity,
            isAgent: false,
            device_type: GLOBAL_MEETING_DATA.device_type,
        };

        msg = { ...msg, ...extraInfo };
        sendDataTrack(msg);
    }
};

// For handling reciecved 207 data track info
export const handleUpdateUserLiveLocationInfo = (msg) => {
    const { updateLatLong } = reduxStore.getState().liveLocation;
    const updateLatLongArray = [...updateLatLong];

    if (msg.latitude != "" && msg.longitude != "" && msg.latitude != 0 && msg.longitude != 0) {
        updateLatLongArray.push({
            identity: msg.identity,
            lat: parseFloat(msg.latitude),
            long: parseFloat(msg.longitude),
        });
        reduxStore.dispatch(setUpdateLatLong(updateLatLongArray));
    }

    reduxStore.dispatch(setIsLatLongDisable(updateLatLongArray.length === 0));

    //202 or 207 - receiving whom first is not consistent every time
    locationPermissionToastMsg(msg);

    //add user if doesn't exist in the call parti list
    addCallParticipant(msg, "notification");

    showLatLongForStreaming();
};

/** distance calculation using the ‘Haversine’ formula. */
export const getDistanceFromLatLonInMeters = (lat1, lon1, lat2, lon2) => {
    let R = 6371; // Radius of the earth in km
    let dLat = deg2rad(lat2 - lat1); // deg2rad below
    let dLon = deg2rad(lon2 - lon1);
    let a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let d = R * c * 1000; // Distance in km multiplied by 1000 for meters
    return d;
};

/**converts degree to radius */
export const deg2rad = (deg) => {
    return deg * (Math.PI / 180);
};

// for showing location_blocked_by_user toast message to agent
export const locationPermissionToastMsg = (msg) => {
    const { joinedUserUserId } = reduxStore.getState().commonReducer;
    if (GLOBAL_MEETING_DATA.isAgent) {
        let toastMsgShownForUser = joinedUserUserId.includes(msg.identity);
        let isLatOrLogEmpty =
            msg.longitude == undefined ||
            msg.longitude == 0 ||
            msg.longitude == "" ||
            msg.latitude == undefined ||
            msg.latitude == 0 ||
            msg.latitude == "";

        if (!toastMsgShownForUser) {
            if (!msg.allowedLocation && PublicVariables.featureFlag.ENABLELOCATION === ENABLED && isLatOrLogEmpty) {
                CustomToast.error(Localize[PublicVariables.currentLanguageObj.languageCode]["location_blocked_by_user"]);
            }

            const updatedJoinedUserUserId = [...joinedUserUserId];
            updatedJoinedUserUserId.push(msg.identity);
            reduxStore.dispatch(setJoinedUserUserId(updatedJoinedUserUserId));
        }
    }
};

export const handleLocationInfoOnJoin = () => {
    if (GLOBAL_MEETING_DATA.isGuest) {
        if (PublicVariables.featureFlag.ENABLELOCATION === ENABLED) {
            getCurrentPosition()
                .then(function (position) {
                    sendLocationInfo({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        diff: 0,
                    });
                    // Handle successful position retrieval
                    showLocationSuccess(position);
                })
                .catch(function (error) {
                    // Handle errors in next task
                    showLocationError(error);
                });
        }
    }
};

// Check updated user location and if changed than send updated location info to agent
// old function checkMyLocationUpdated
export const checkUserUpdatedLocation = () => {
    PublicVariables.locationIntervalId = setInterval(() => {
        if (PublicVariables.allowedLocation === true) {
            console.log("update location on every 10 sec interval code here");

            const { stableLocationCount } = reduxStore.getState().liveLocation;
            const { isRemoteParticipantJoined } = reduxStore.getState().commonReducer;

            if (isRemoteParticipantJoined) {
                getUpdatedCoordinates().then(function (result) {
                    let lat1 = CALL_DATA.Latitude;
                    let lon1 = CALL_DATA.Longitude;
                    let lat2 = result.latitude;
                    let lon2 = result.longitude;
                    if (lat1 > 0) {
                        let locDifference = getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2);

                        let updatedStableLocationCount = stableLocationCount;
                        updatedStableLocationCount++;
                        reduxStore.dispatch(setStableLocationCount(updatedStableLocationCount));

                        //if location updated send message in room
                        if (Math.floor(locDifference) >= 10 && updatedStableLocationCount > 3) {
                            CALL_DATA.Latitude = lat2;
                            CALL_DATA.Longitude = lon2;

                            if (!GLOBAL_MEETING_DATA.isAgent) {
                                /* 207 notification for sending */
                                sendLocationInfo({
                                    message: "from loc update",
                                    latitude: lat2,
                                    longitude: lon2,
                                    diff: locDifference,
                                });
                            }
                        }
                    }
                });
            }
        }
    }, 10000);
};

export const getSelectedMapViewId = () => {
    const MAP_TYPES = {
        roadmap: "roadmap",
        satellite: "satellite",
        hybrid: "hybrid",
        terrain: "terrain",
    };

    const mapIdInLocal = storage.get(LOCAL_STORAGE_KEYS.MAP_TYPE_ID);
    let mapType = MAP_TYPES.roadmap;

    if (mapIdInLocal && mapIdInLocal in MAP_TYPES) {
        mapType = MAP_TYPES[mapIdInLocal];
    }

    return mapType;
};

export const handleOnMapTypeViewChange = (mapId) => {
    const { mapTypeId } = reduxStore.getState().liveLocation;

    if (!mapId || (mapId && mapId === mapTypeId)) return;
    reduxStore.dispatch(setMapTypeId(mapId));

    setTimeout(() => {
        const { isCallDisconnected } = reduxStore.getState().commonReducer;
        if (!isCallDisconnected) {
            storage.set(LOCAL_STORAGE_KEYS.MAP_TYPE_ID, mapId);
        }
    }, 1000);
};

export const isLiveLocationButtonIsVisible = () => {
    return GLOBAL_MEETING_DATA.isAgent && PublicVariables.featureFlag.LIVEMAPTRACKING === ENABLED;
};

// fetching image URL based on polyline point
export const callFetchingMapImage = (poly, drawRouteData = []) => {
    // path is an MVCArray
    // get polyline all point or path using getPath method

    let path;
    let imgUrl;
    if (poly) {
        path = poly.getPath();
    }
    let allPointEncoded = "";
    if (path) {
        // if path available
        const encodeString = window.google.maps.geometry.encoding.encodePath(path);
        allPointEncoded = "enc:" + encodeURIComponent(encodeString);
    } else {
        // if path not available
        LogFirebase("LiveMap", "URL", "Poly line not available");
    }
    // end point marker in live image

    const startingLatLong = drawRouteData[0];
    const endingLatLong = drawRouteData.at(-1);

    if (drawRouteData.length > 1) {
        imgUrl =
            MAP_IMAGE_URL +
            endingLatLong.lat +
            "," +
            endingLatLong.lng +
            "&" +
            START_MARKER +
            startingLatLong.lat +
            "," +
            startingLatLong.lng +
            "&" +
            END_MARKER +
            endingLatLong.lat +
            "," +
            endingLatLong.lng +
            "&path=color:0x6495ED|" +
            allPointEncoded +
            GOOGLE_MAP_EXTRA_OPTIONS +
            MAP_IMAGE_KEY;
    } else {
        imgUrl =
            MAP_IMAGE_URL +
            startingLatLong.lat +
            "," +
            startingLatLong.lng +
            "&" +
            START_MARKER +
            startingLatLong.lat +
            "," +
            startingLatLong.lng +
            "&" +
            END_MARKER +
            endingLatLong.lat +
            "," +
            endingLatLong.lng +
            "&path=color:0x6495ED|" +
            allPointEncoded +
            GOOGLE_MAP_EXTRA_OPTIONS +
            MAP_IMAGE_KEY;
    }

    return imgUrl;
};

// take live map image Screen shot
export const takeLiveMapImage = (poly, drawRouteData = []) => {
    let takePhotoPromiseResolver;
    let takePhotoPromiseRejecter;

    const localisedValues = Localize[PublicVariables.currentLanguageObj.languageCode];

    const takePhotoPromise = new Promise((resolve, reject) => {
        takePhotoPromiseResolver = resolve;
        takePhotoPromiseRejecter = reject;
    });

    CustomToast.promise(takePhotoPromise, {
        pending: localisedValues["uploading_image"],
        success: localisedValues["successfully_uploaded"],
        error: localisedValues["location_image_upload_failed"],
    });

    reduxStore.dispatch(setIsSaveBtnDisabled(true));

    // call back function for fetching polyline image URL
    let imgUrl = callFetchingMapImage(poly, drawRouteData);
    let canvas_Height;

    const startingLatLong = drawRouteData[0];
    const endingLatLong = drawRouteData.at(-1);

    const width = 640;
    const height = 640;
    const orientation = "3";
    const watermarkImg = new Image();

    if (drawRouteData.length > 1) {
        LogFirebase("LiveMap", "upload", "multiple position in map");
        console.log("multiple point avialble");

        let objVal = watermarkImageService.__watermarkImage.fetchImageWaterMark({
            screenType: "location",
            width,
            height,
            localOrientation: orientation,
            localSS: false,
            localVideo: false,
            bookmark: false,
            fromLiveLocation: "livemap",
            liveLat: endingLatLong.lat,
            liveLong: endingLatLong.lng,
        });

        const { imageSrc, canvasHeight } = objVal;
        canvas_Height = canvasHeight;
        watermarkImg.src = imageSrc;

        LogFirebase("LiveMap", "multiple position upload URL", imgUrl);
    } else {
        LogFirebase("LiveMap", "upload", "only start position in map");
        console.log("only start point avialble");
        imgUrl =
            MAP_IMAGE_URL +
            startingLatLong.lat +
            "," +
            startingLatLong.lng +
            "&" +
            START_MARKER +
            startingLatLong.lat +
            "," +
            startingLatLong.lng +
            GOOGLE_MAP_EXTRA_OPTIONS +
            MAP_IMAGE_KEY;

        LogFirebase("LiveMap", "start position upload URL", imgUrl);

        let objVal = watermarkImageService.__watermarkImage.fetchImageWaterMark({
            screenType: "location",
            width,
            height,
            localOrientation: orientation,
            localSS: false,
            localVideo: false,
            bookmark: false,
            fromLiveLocation: "livemap",
            liveLat: startingLatLong.lat,
            liveLong: startingLatLong.lng,
        });

        const { imageSrc, canvasHeight } = objVal;
        canvas_Height = canvasHeight;
        watermarkImg.src = imageSrc;
    }

    fetch(imgUrl).then(handleResponseBlob).catch(handleFetchError);

    function handleResponseBlob(response) {
        return response.blob().then(processBlob);
    }

    function processBlob(blob) {
        console.log("LiveMap Loaded");
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => handleReaderLoad(reader);
    }

    function handleReaderLoad(reader) {
        LogFirebase("LiveMap", "Loaded", "");

        const mapImg = new Image();
        const base64data = reader.result;
        mapImg.src = base64data;

        const canvas = document.getElementById("screenshotcanvas");
        canvas.height = canvas_Height;
        canvas.width = width;

        LogFirebase("LiveMap", "mapsize", { height: canvas_Height, width: width });
        console.log("LiveMap mapsize", { height: canvas_Height, width: width });

        const context = canvas.getContext("2d");

        mapImg.onload = () => handleMapImageLoad(context, mapImg);
    }

    function handleMapImageLoad(context, mapImg) {
        // merge map and watermark
        context.drawImage(mapImg, 0, 0);
        context.drawImage(watermarkImg, 0, 0);

        const imgData = context.canvas.toDataURL("image/jpeg");
        const blobFinal = watermarkImageService.__base64ToBlobImage.convertData(imgData);

        takePhotoService.uploadImage("location", blobFinal, null, handleUploadResponse);
    }

    function handleUploadResponse(response) {
        const result = response.data;

        if (response.success > 0) {
            takePhotoPromiseResolver();

            if (result?.thumbUrl) {
                takePhotoService.addThumbInList(result.thumbUrl, result.fileUrl, result.fileName);
            }

            LogFirebase("LiveMap", "upload success", { mapURL: result?.thumbUrl });
            reduxStore.dispatch(setIsSaveBtnDisabled(false));
            return;
        }

        handleUploadError(result);
    }

    function handleUploadError(result) {
        takePhotoPromiseRejecter();
        reduxStore.dispatch(setIsSaveBtnDisabled(false));
        LogFirebase("LiveMap", "upload failed", "");
        LogFirebase("LiveMap", "upload error", {
            status: "fail",
            isMapImage: true,
            mapURL: imgUrl,
            error: result.error,
        });
        console.log("location image upload failed", result.error);
    }

    function handleFetchError(error) {
        takePhotoPromiseRejecter();
        reduxStore.dispatch(setIsSaveBtnDisabled(false));
        LogFirebase("LiveMap", "image_downloading_status", { status: "FAILED", error: error });
        console.log("LiveMap image_downloading_status FAILED", error);
    }
};
